/**
 * 阅读模式排版设置。
 *
 * 此前字号/行距/边距/主题全部是 CSS 常量（`reader.css:174-178` 用全局
 * `--font-size-article`，`reader.css:139` 宽度写死 720px），用户完全不可调；
 * 长文阅读体验受此影响很大 —— 手机上一屏字数、夜间亮度、行距舒适度都无法适配。
 *
 * 存储策略：与其它偏好一致（服务端 `user_preferences`），本模块负责
 * 归一化、CSS 变量应用与读写。localStorage 作为首屏兜底避免闪烁。
 */
import { storageGet, storageSet } from '../../utils/storage';

export type ReaderTheme = 'light' | 'sepia' | 'eye' | 'dark';

export interface ReaderSettings {
  /** 正文字号（px） */
  fontSize: number;
  /** 行高倍数 */
  lineHeight: number;
  /** 正文区宽度（px） */
  contentWidth: number;
  /** 页面水平内边距（px） */
  pagePadding: number;
  /** 段落是否首行缩进 2 字符 */
  indent: boolean;
  /** 主题（含夜间与护眼） */
  theme: ReaderTheme;
}

export const DEFAULT_READER_SETTINGS: ReaderSettings = {
  fontSize: 18,
  lineHeight: 1.9,
  contentWidth: 720,
  pagePadding: 28,
  indent: true,
  theme: 'light',
};

/** 可取值的范围（同时用于 UI 控件与归一化校验） */
export const READER_RANGES = {
  fontSize: { min: 14, max: 30, step: 1 },
  lineHeight: { min: 1.4, max: 2.6, step: 0.1 },
  contentWidth: { min: 480, max: 1000, step: 20 },
  pagePadding: { min: 12, max: 64, step: 4 },
} as const;

export const THEME_LABELS: Record<ReaderTheme, string> = {
  light: '明亮',
  sepia: '米黄',
  eye: '护眼',
  dark: '夜间',
};

const SETTINGS_KEY = 'reader_settings';
/** 服务端偏好名（与其它偏好统一） */
export const READER_SETTINGS_PREF = 'READER_SETTINGS';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

const isTheme = (v: unknown): v is ReaderTheme =>
  v === 'light' || v === 'sepia' || v === 'eye' || v === 'dark';

/**
 * 归一化任意来源的设置：字段缺失/类型错误/越界都回落到默认值。
 * 偏好可能被旧版本写入或手工改坏，导出/阅读不能因此崩掉。
 */
export function normalizeReaderSettings(raw: unknown): ReaderSettings {
  const input = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof ReaderSettings, unknown>>;
  const num = (value: unknown, fallback: number, range: { min: number; max: number }): number => {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return fallback;
    return clamp(n, range.min, range.max);
  };
  return {
    fontSize: num(input.fontSize, DEFAULT_READER_SETTINGS.fontSize, READER_RANGES.fontSize),
    lineHeight: num(input.lineHeight, DEFAULT_READER_SETTINGS.lineHeight, READER_RANGES.lineHeight),
    contentWidth: num(input.contentWidth, DEFAULT_READER_SETTINGS.contentWidth, READER_RANGES.contentWidth),
    pagePadding: num(input.pagePadding, DEFAULT_READER_SETTINGS.pagePadding, READER_RANGES.pagePadding),
    indent: typeof input.indent === 'boolean' ? input.indent : DEFAULT_READER_SETTINGS.indent,
    theme: isTheme(input.theme) ? input.theme : DEFAULT_READER_SETTINGS.theme,
  };
}

/** 读取本地缓存的设置（首屏立即生效，避免闪烁） */
export function loadLocalReaderSettings(): ReaderSettings {
  return normalizeReaderSettings(storageGet<unknown>(SETTINGS_KEY, DEFAULT_READER_SETTINGS));
}

/** 写入本地缓存 */
export function saveLocalReaderSettings(settings: ReaderSettings): void {
  storageSet(SETTINGS_KEY, settings);
}

/**
 * 把设置写成 CSS 变量，由 `reader.css` 消费。
 * 通过元素上的 `data-reader-theme` 切换主题色板，避免为每个主题写一套类名。
 */
export function applyReaderSettings(el: HTMLElement | null, settings: ReaderSettings): void {
  if (!el) return;
  el.style.setProperty('--reader-font-size', `${settings.fontSize}px`);
  el.style.setProperty('--reader-line-height', String(settings.lineHeight));
  el.style.setProperty('--reader-content-width', `${settings.contentWidth}px`);
  el.style.setProperty('--reader-page-padding', `${settings.pagePadding}px`);
  el.style.setProperty('--reader-text-indent', settings.indent ? '2em' : '0');
  el.dataset.readerTheme = settings.theme;
}

/** 计算滚动进度百分比（0-100） */
export function scrollProgress(scrollTop: number, scrollHeight: number, clientHeight: number): number {
  const scrollable = scrollHeight - clientHeight;
  if (scrollable <= 0) return 100; // 内容不足一屏，视为已读完
  return Math.min(100, Math.max(0, Math.round((scrollTop / scrollable) * 100)));
}
